/**
 * 首页「今日待跟进」（顾问陪跑 A-1 销售工作台）。
 *
 * 销售每天打开后台要回答的问题只有一个：今天该跟进谁、跟进什么？
 * 这里把名下所有书里「在等销售动手的事」按紧急程度分类列出来，点客户进详情，点「查看全部」进带好筛选的列表。
 * 超管看全部客户，并额外看到「名下还有客户、但归属人账号已停用 / 不再是销售 / 已删除」的提醒——这些客户没人管。
 *
 * 没有顾问点位的角色（运营、客服等）不渲染这块，也不请求接口。
 */
import { Link } from 'react-router-dom';
import { Alert, Card, Empty, List, Space, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../contracts/permissions';
import { getAdvisorWorkbench, type DisabledOwnerDto } from '../../api/advisor';
import { ATTENTION_META, ATTENTION_ORDER, workbenchDetail } from '../advisor/customers/stage';

const { Text } = Typography;

const OWNER_REASON: Record<DisabledOwnerDto['reason'], string> = {
  disabled: '账号已停用',
  not_sales: '账号已不是销售',
  missing: '账号已删除',
};

export function SalesWorkbench() {
  const canRead = usePermission(PERMISSIONS.ADVISOR_CUSTOMER_READ);
  const query = useQuery({
    queryKey: ['advisor-workbench'],
    queryFn: getAdvisorWorkbench,
    enabled: canRead,
    retry: false,
  });
  if (!canRead) return null;

  const data = query.data;
  const unassigned = data?.unassignedOwners ?? [];
  const unassignedCustomers = unassigned.reduce((sum, o) => sum + o.customerCount, 0);
  const total = data ? ATTENTION_ORDER.reduce((sum, a) => sum + data.counts[a], 0) : 0;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%', marginTop: 16 }} data-testid="sales-workbench">
      {unassigned.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`有 ${unassignedCustomers} 位客户没人跟进，请尽快转交`}
          description={
            <Space direction="vertical" size={2}>
              {unassigned.map((o) => (
                <Text key={o.advisorAdminId}>
                  {o.nickname ?? o.advisorAdminId}（{OWNER_REASON[o.reason]}）名下还有 {o.customerCount} 位客户
                </Text>
              ))}
              <Link to="/advisor/sales">去销售管理转交 →</Link>
            </Space>
          }
        />
      )}

      <Card
        title="今日待跟进"
        loading={query.isLoading}
        extra={<Link to="/advisor/customers">全部客户</Link>}
      >
        {query.isError ? (
          <Text type="secondary">待跟进列表暂时加载失败，可以直接进「顾问陪跑 / 客户」查看。</Text>
        ) : total === 0 ? (
          <Empty description="今天没有需要跟进的客户" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {ATTENTION_ORDER.filter((a) => (data?.counts[a] ?? 0) > 0).map((a) => (
              <div key={a} data-attention={a}>
                <Space size={8} wrap style={{ marginBottom: 4 }}>
                  <Tag color={ATTENTION_META[a].color}>{ATTENTION_META[a].label}</Tag>
                  <Text strong>{data!.counts[a]}</Text>
                  <Text type="secondary">{ATTENTION_META[a].hint}</Text>
                  <Link to={`/advisor/customers?attention=${a}`}>查看全部</Link>
                </Space>
                <List
                  size="small"
                  dataSource={data!.items[a]}
                  renderItem={(item) => (
                    <List.Item>
                      <Space size={8} wrap>
                        <Link to={`/advisor/customers/${encodeURIComponent(item.customerId)}`}>{item.displayName}</Link>
                        {item.bookLabel && <Text type="secondary">{item.bookLabel}</Text>}
                        <Text>{workbenchDetail(item)}</Text>
                        {item.advisorNickname && <Tag>{item.advisorNickname}</Tag>}
                      </Space>
                    </List.Item>
                  )}
                />
                {data!.counts[a] > data!.items[a].length && (
                  <Text type="secondary">
                    还有 {data!.counts[a] - data!.items[a].length} 条，
                    <Link to={`/advisor/customers?attention=${a}`}>查看全部</Link>
                  </Text>
                )}
              </div>
            ))}
          </Space>
        )}
      </Card>
    </Space>
  );
}
